import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil, switchMap, catchError, map } from 'rxjs/operators';
import { StockMovementsService, ConfirmEntryReceiptPayload } from '../../../../services/stock-movements.service';
import { ItemService } from '../../../../services/item.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import {
  PurchaseOrderService,
  PurchaseRequestSimpleViewResponse
} from '../../../../services/purchase-order.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { StockMovement } from '../../../../models/StockMovement';
import { PurchaseOrder } from '../../../../models/PurchaseOrder';

/** Resumo do pedido para o painel lateral do recebimento. */
export interface ReceiptPurchaseOrderContext {
  id: number;
  orderLabel: string;
  status?: string;
  supplierName?: string;
  expectedDelivery?: string;
  description?: string;
  notes?: string;
  itemsSummary?: string;
  /** Quantidade máxima ainda permitida na linha do pedido (pendente / restante), quando identificada. */
  orderLineMaxQuantity?: number;
  /** Produto da linha do pedido vinculada (nome/código do item pedido, não o número do pedido). */
  linkedLineProductName?: string;
  linkedLineProductCode?: string;
}

@Component({
  selector: 'app-stock-entry-receipt',
  templateUrl: './stock-entry-receipt.component.html',
  styleUrls: ['./stock-entry-receipt.component.css']
})
export class StockEntryReceiptComponent implements OnInit, OnDestroy {
  movement: StockMovement | null = null;
  /** Cabeçalho do pedido de compra (quando a movimentação veio de compras). */
  purchaseOrderContext: ReceiptPurchaseOrderContext | null = null;
  isLoading = true;
  isSubmitting = false;
  showSuccess = false;
  movementId: number | null = null;

  /** Limite superior da quantidade recebida (movimentação e, se houver, linha do pedido). */
  maxReceivableQuantity = 0;

  receiptForm: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private stockMovementsService: StockMovementsService,
    private itemService: ItemService,
    private warehousesService: WarehousesService,
    private purchaseOrderService: PurchaseOrderService,
    private errorService: ErrorService
  ) {
    this.receiptForm = this.fb.group({
      receivedQuantity: [null, [Validators.required, Validators.min(0.0001)]],
      conferenceNote: [''],
      receipt: this.fb.group({
        documentNumber: ['', Validators.required],
        documentSeries: [''],
        invoiceAccessKey: [''],
        fiscalDocumentUrl: [''],
        note: ['']
      })
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = Number(params.get('id'));
      if (!id) {
        this.router.navigate(['/stock/movements']);
        return;
      }
      this.movementId = id;
      this.loadMovement(id);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMovement(id: number): void {
    this.isLoading = true;
    this.purchaseOrderContext = null;
    this.stockMovementsService
      .getById(id)
      .pipe(
        switchMap((m) => this.hydrateMovementForReceipt(m)),
        switchMap((m) => this.hydratePurchaseOrderPanel(m)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ({ movement, purchaseOrder }) => {
          let mov = movement;
          const lineName = purchaseOrder?.linkedLineProductName?.trim();
          if (lineName) {
            const hasName = (mov.itemName || mov.stockName || '').trim();
            if (!hasName) {
              const hasCode = (mov.itemCode || mov.stockCode || '').trim();
              const lineCode = purchaseOrder?.linkedLineProductCode?.trim();
              mov = {
                ...mov,
                itemName: lineName,
                ...(!hasCode && lineCode ? { itemCode: lineCode } : {})
              };
            }
          }
          this.movement = mov;
          this.purchaseOrderContext = purchaseOrder;
          if (!this.canReceive(mov)) {
            this.isLoading = false;
            this.errorService.showErrorMessage(
              'Não é possível registrar o recebimento desta movimentação no momento.'
            );
            this.router.navigate(['/stock/movements']);
            return;
          }
          this.maxReceivableQuantity = this.computeMaxReceivableQuantity(mov, purchaseOrder);
          const qtyCtrl = this.receiptForm.get('receivedQuantity');
          const defaultQty = Math.min(
            Number(mov.quantity) || this.maxReceivableQuantity,
            this.maxReceivableQuantity
          );
          qtyCtrl?.setValidators([
            Validators.required,
            Validators.min(0.0001),
            Validators.max(this.maxReceivableQuantity)
          ]);
          qtyCtrl?.setValue(defaultQty);
          qtyCtrl?.updateValueAndValidity({ emitEvent: false });
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorService.showErrorMessage('Não foi possível carregar a movimentação.');
          this.router.navigate(['/stock/movements']);
        }
      });
  }

  /** Carrega dados do pedido de compra para exibir no contexto do recebimento. */
  private hydratePurchaseOrderPanel(
    m: StockMovement
  ): Observable<{ movement: StockMovement; purchaseOrder: ReceiptPurchaseOrderContext | null }> {
    const pid = m.purchaseOrderId;
    if (pid == null || pid <= 0) {
      return of({ movement: m, purchaseOrder: null });
    }
    return this.purchaseOrderService.getPurchaseOrderById(pid).pipe(
      map((po) => ({
        movement: {
          ...m,
          purchaseOrderNumber: (m.purchaseOrderNumber || '').trim() || po.orderNumber
        },
        purchaseOrder: this.mapPurchaseOrderToReceiptContext(po, m)
      })),
      catchError(() =>
        this.purchaseOrderService.getPurchaseRequestSimpleView(pid).pipe(
          map((sv) => ({
            movement: m,
            purchaseOrder: this.mapSimplePurchaseRequestToReceiptContext(sv, m)
          })),
          catchError(() => of({ movement: m, purchaseOrder: null }))
        )
      )
    );
  }

  private mapPurchaseOrderToReceiptContext(
    po: PurchaseOrder,
    movement: StockMovement
  ): ReceiptPurchaseOrderContext {
    const items = Array.isArray(po.items) ? po.items : [];
    const lineId = movement.purchaseOrderItemId;
    let orderLineMaxQuantity: number | undefined;
    let linkedLineProductName: string | undefined;
    let linkedLineProductCode: string | undefined;
    if (items.length > 0) {
      let line: (typeof items)[0] | undefined;
      if (lineId != null && lineId > 0) {
        line = items.find((it) => it.id === lineId);
      }
      if (!line && movement.itemId != null && movement.itemId > 0) {
        line = items.find((it) => it.itemId === movement.itemId);
      }
      if (line) {
        const pending = Number(line.quantityPending);
        const approved = Number(line.quantityApproved);
        const requested = Number(line.quantityRequested);
        const recv = Number(line.quantityReceived);
        const recvOk = Number.isFinite(recv) ? recv : 0;
        if (Number.isFinite(pending) && pending > 0) {
          orderLineMaxQuantity = pending;
        } else if (Number.isFinite(approved) && approved > 0) {
          orderLineMaxQuantity = Math.max(0, approved - recvOk);
        } else if (Number.isFinite(requested) && requested > 0) {
          orderLineMaxQuantity = Math.max(0, requested - recvOk);
        }
        if (orderLineMaxQuantity !== undefined && orderLineMaxQuantity <= 0) {
          orderLineMaxQuantity = undefined;
        }
        const pn = (line.productName || line.itemName || '').trim();
        const pc = (line.productCode || '').trim();
        if (pn) {
          linkedLineProductName = pn;
        }
        if (pc) {
          linkedLineProductCode = pc;
        }
      }
    }
    const parts = items.slice(0, 6).map((it) => {
      const name = (it.productName || it.itemName || '').trim();
      const q = it.quantityRequested ?? it.quantityApproved;
      const u = (it.unit || '').trim();
      if (!name) {
        return '';
      }
      return u ? `${name} (${q ?? '—'} ${u})` : `${name} (${q ?? '—'})`;
    });
    const itemsSummary = parts.filter(Boolean).join(' · ');
    let expectedDelivery: string | undefined;
    if (po.expectedDeliveryDate) {
      const d = new Date(po.expectedDeliveryDate as unknown as string);
      if (!Number.isNaN(d.getTime())) {
        expectedDelivery = d.toLocaleDateString('pt-BR');
      }
    }
    return {
      id: po.id,
      orderLabel: (po.orderNumber || '').trim() ? String(po.orderNumber).trim() : `Pedido #${po.id}`,
      status: po.status,
      supplierName: po.supplierName,
      expectedDelivery,
      description: po.description,
      notes: po.notes,
      itemsSummary: itemsSummary || undefined,
      ...(orderLineMaxQuantity !== undefined ? { orderLineMaxQuantity } : {}),
      ...(linkedLineProductName ? { linkedLineProductName } : {}),
      ...(linkedLineProductCode ? { linkedLineProductCode } : {})
    };
  }

  private mapSimplePurchaseRequestToReceiptContext(
    sv: PurchaseRequestSimpleViewResponse,
    _movement: StockMovement
  ): ReceiptPurchaseOrderContext {
    const list = sv.items ?? [];
    const parts = list.slice(0, 6).map((it) => `${it.productName} (${it.quantityRequested} ${it.unit})`);
    let linkedLineProductName: string | undefined;
    if (list.length === 1) {
      const pn = (list[0].productName || '').trim();
      if (pn) {
        linkedLineProductName = pn;
      }
    }
    return {
      id: sv.id,
      orderLabel: `Solicitação #${sv.id}`,
      status: sv.status,
      description: sv.description,
      notes: sv.notes,
      itemsSummary: parts.length ? parts.join(' · ') : undefined,
      ...(linkedLineProductName ? { linkedLineProductName } : {})
    };
  }

  goToPurchaseOrder(poId: number): void {
    this.router.navigate(['/purchase-orders', poId]);
  }

  /** API por vezes devolve só `itemId`; priorizamos nome/código do cadastro de Itens. */
  private hydrateMovementForReceipt(m: StockMovement): Observable<StockMovement> {
    return of(m).pipe(
      switchMap((x) => {
        if (x.itemId == null || x.itemId <= 0) {
          return of(x);
        }
        return this.itemService.getItemById(x.itemId).pipe(
          map((it) => ({
            ...x,
            itemName: (it.name || '').trim() || (x.itemName || '').trim() || x.itemName,
            itemCode: (it.code || '').trim() || (x.itemCode || '').trim() || x.itemCode
          })),
          catchError(() => of(x))
        );
      }),
      switchMap((x) => {
        const originOk = (x.originWarehouseName || '').trim() && (x.originWarehouseName || '').trim() !== '-';
        if (originOk) {
          return of(x);
        }
        if (x.originWarehouseId != null && x.originWarehouseId > 0) {
          return this.warehousesService.getById(x.originWarehouseId).pipe(
            map((w) => ({ ...x, originWarehouseName: w?.name || x.originWarehouseName })),
            catchError(() => of(x))
          );
        }
        if (x.destinationWarehouseId != null && x.destinationWarehouseId > 0) {
          return this.warehousesService.getById(x.destinationWarehouseId).pipe(
            map((w) => ({
              ...x,
              originWarehouseName: (x.originWarehouseName || '').trim() || w?.name || x.originWarehouseName
            })),
            catchError(() => of(x))
          );
        }
        return of(x);
      })
    );
  }

  private computeMaxReceivableQuantity(
    m: StockMovement,
    poCtx: ReceiptPurchaseOrderContext | null
  ): number {
    const mov = Number(m.quantity);
    if (!Number.isFinite(mov) || mov <= 0) {
      return 0.0001;
    }
    const fromPo = poCtx?.orderLineMaxQuantity;
    if (fromPo != null && Number.isFinite(fromPo) && fromPo > 0) {
      return Math.min(mov, fromPo);
    }
    return mov;
  }

  private canReceive(m: StockMovement): boolean {
    return this.stockMovementsService.isOpenForEntryReceipt(m);
  }

  onBack(): void {
    this.router.navigate(['/stock/movements']);
  }

  isFieldInvalid(name: string): boolean {
    const c = this.receiptForm.get(name);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  isReceiptFieldInvalid(name: string): boolean {
    const c = this.receiptForm.get(`receipt.${name}`);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  onDocumentFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (file) {
      const g = this.receiptForm.get('receipt');
      const current = (g?.get('fiscalDocumentUrl')?.value as string) || '';
      if (!current.trim()) {
        g?.patchValue({ fiscalDocumentUrl: `stock/${file.name}` }, { emitEvent: false });
      }
    }
  }

  /** Máscara visual da chave de acesso (NFe — até 44 dígitos, grupos de 4). */
  onNfeAccessKeyInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const digits = el.value.replace(/\D/g, '').slice(0, 44);
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    this.receiptForm.get('receipt')?.get('invoiceAccessKey')?.setValue(formatted, { emitEvent: false });
    el.value = formatted;
  }

  onSubmit(): void {
    if (this.receiptForm.invalid || !this.movementId) {
      this.receiptForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const payload = this.buildPayload();
    this.stockMovementsService
      .confirmEntryReceipt(this.movementId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.showSuccess = true;
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  private buildPayload(): ConfirmEntryReceiptPayload {
    const v = this.receiptForm.getRawValue() as {
      receivedQuantity: number;
      conferenceNote: string;
      receipt: {
        documentNumber: string;
        documentSeries: string;
        invoiceAccessKey: string;
        fiscalDocumentUrl: string;
        note: string;
      };
    };
    const r = v.receipt;
    const rKey = (r.invoiceAccessKey || '').replace(/\s/g, '');
    const cap =
      this.maxReceivableQuantity > 0 ? this.maxReceivableQuantity : Number(this.movement?.quantity) || 0;
    let receivedQty = Number(v.receivedQuantity);
    if (Number.isFinite(cap) && cap > 0 && receivedQty > cap) {
      receivedQty = cap;
    }

    return {
      receivedQuantity: receivedQty,
      conferenceNote: (v.conferenceNote || '').trim() || null,
      purchaseOrderId:
        this.movement?.purchaseOrderId != null && this.movement.purchaseOrderId > 0
          ? this.movement.purchaseOrderId
          : null,
      purchaseOrderItemId:
        this.movement?.purchaseOrderItemId != null && this.movement.purchaseOrderItemId > 0
          ? this.movement.purchaseOrderItemId
          : null,
      receiptStatus: null,
      receiptRowNotes: null,
      receivedAt: null,
      receivedByStaffId: null,
      invoiceAccessKey: rKey || null,
      ...(this.movement?.itemId != null && this.movement.itemId > 0 ? { itemId: this.movement.itemId } : {}),
      receipt: {
        documentNumber: r.documentNumber,
        documentSeries: (r.documentSeries || '').trim() || null,
        invoiceAccessKey: rKey || null,
        fiscalDocumentUrl: (r.fiscalDocumentUrl || '').trim() || null,
        note: (r.note || '').trim() || null
      }
    };
  }

  getItemDisplayName(m: StockMovement): string {
    const name = (m.linkedItemName || m.itemName || m.stockName || '').trim();
    if (name) {
      return name;
    }
    const poName = this.purchaseOrderContext?.linkedLineProductName?.trim();
    if (poName) {
      return poName;
    }
    if (m.itemId != null && m.itemId > 0) {
      return `Item #${m.itemId}`;
    }
    if (m.linkedItemId != null && m.linkedItemId > 0) {
      return `Item #${m.linkedItemId}`;
    }
    return 'Não informado';
  }

  getItemDisplayCode(m: StockMovement): string | null {
    const code = (m.linkedItemCode || m.itemCode || m.stockCode || '').trim();
    if (code) {
      return code;
    }
    return this.purchaseOrderContext?.linkedLineProductCode?.trim() || null;
  }

  getReceivedQtyErrorMessage(): string {
    const c = this.receiptForm.get('receivedQuantity');
    if (!c || !c.errors || !(c.dirty || c.touched)) {
      return '';
    }
    if (c.errors['max']) {
      return `Não pode ser maior que ${this.maxReceivableQuantity} (limite da movimentação / pedido).`;
    }
    if (c.errors['min']) {
      return 'Informe um valor maior que zero.';
    }
    if (c.errors['required']) {
      return 'Informe a quantidade recebida.';
    }
    return '';
  }

  onReceivedQuantityInput(): void {
    const c = this.receiptForm.get('receivedQuantity');
    if (!c) {
      return;
    }
    const cap = this.maxReceivableQuantity > 0 ? this.maxReceivableQuantity : Number(this.movement?.quantity) || 0;
    const raw = Number(c.value);
    if (!Number.isFinite(raw) || cap <= 0) {
      return;
    }
    if (raw > cap) {
      c.setValue(cap, { emitEvent: false });
    }
  }

  formatOrigin(m: StockMovement): string {
    const o = (m.originWarehouseName ?? '').trim();
    if (o && o !== '-') {
      return o;
    }
    const d = (m.destinationWarehouseName ?? '').trim();
    if (d && d !== '-') {
      return d;
    }
    return 'Não informado';
  }

  /** Texto sob o nome do item: movimentação / OS — o pedido fica só no painel «Pedido de compra». */
  getItemReceiptAuxLine(m: StockMovement): string {
    const parts = [`Movimentação #${m.id}`];
    if (m.serviceOrderNumber) {
      parts.push(`OS ${m.serviceOrderNumber}`);
    } else if (m.serviceOrderId) {
      parts.push(`OS #${m.serviceOrderId}`);
    }
    return parts.join(' · ');
  }
}
